import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Upload,
  Mail,
  Phone,
  Briefcase,
  Clock,
  Settings as SettingsIcon,
  Check,
  Lock,
  Key,
  Send,
  Plus,
  Trash2
} from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import toast from 'react-hot-toast';
import { StaffFormData } from '../types';

interface Service {
  id: string;
  name_he: string;
  price: number;
  duration: string;
}

interface StaffSpecialty {
  id: string;
  name_he: string;
}

interface StaffFormModalProps {
  isOpen: boolean;
  formData: StaffFormData;
  services: Service[];
  specialties: StaffSpecialty[];
  isEditing: boolean;
  onFormChange: (data: StaffFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
  existingImageUrl?: string;
  busy?: boolean;
}

type TabType = 'basic' | 'services' | 'hours' | 'settings';

const DAYS_ORDER = [
  { key: 'sunday', label: 'ראשון' },
  { key: 'monday', label: 'שני' },
  { key: 'tuesday', label: 'שלישי' },
  { key: 'wednesday', label: 'רביעי' },
  { key: 'thursday', label: 'חמישי' },
  { key: 'friday', label: 'שישי' },
  { key: 'saturday', label: 'שבת' }
];

const DEFAULT_HOURS = {
  sunday: { is_active: true, start_time: '09:00', end_time: '17:00', breaks: [] },
  monday: { is_active: true, start_time: '09:00', end_time: '17:00', breaks: [] },
  tuesday: { is_active: true, start_time: '09:00', end_time: '17:00', breaks: [] },
  wednesday: { is_active: true, start_time: '09:00', end_time: '17:00', breaks: [] },
  thursday: { is_active: true, start_time: '09:00', end_time: '17:00', breaks: [] },
  friday: { is_active: true, start_time: '09:00', end_time: '17:00', breaks: [] },
  saturday: { is_active: false, start_time: '09:00', end_time: '17:00', breaks: [] }
};

export function StaffFormModal({
  isOpen,
  formData,
  services,
  specialties,
  isEditing,
  onFormChange,
  onSubmit,
  onCancel,
  existingImageUrl,
  busy = false
}: StaffFormModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [newSpecialty, setNewSpecialty] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sendingResetLink, setSendingResetLink] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && existingImageUrl) {
      setPreviewUrl(existingImageUrl);
    } else if (!isEditing) {
      setPreviewUrl(null);
    }
  }, [isEditing, existingImageUrl]);

  useEffect(() => {
    if (!isEditing && services.length > 0 && formData.services.length === 0) {
      const updatedServices = services.map(service => ({
        id: service.id,
        price: service.price.toString(),
        duration: service.duration,
        is_active: true
      }));
      onFormChange({ ...formData, services: updatedServices });
    }
  }, [isEditing, services]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFormChange({ ...formData, profile_image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setPreviewUrl(null);
    onFormChange({ ...formData, profile_image: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddSpecialty = () => {
    if (newSpecialty.trim()) {
      onFormChange({
        ...formData,
        specialties: [...(formData.specialties || []), newSpecialty.trim()]
      });
      setNewSpecialty('');
    }
  };

  const handleRemoveSpecialty = (index: number) => {
    const updated = [...(formData.specialties || [])];
    updated.splice(index, 1);
    onFormChange({ ...formData, specialties: updated });
  };

  const handleServiceToggle = (serviceId: string) => {
    const existingService = formData.services.find(s => s.id === serviceId);

    if (existingService) {
      const updated = formData.services.map(s =>
        s.id === serviceId ? { ...s, is_active: !s.is_active } : s
      );
      onFormChange({ ...formData, services: updated });
    } else {
      const service = services.find(s => s.id === serviceId);
      if (service) {
        onFormChange({
          ...formData,
          services: [
            ...formData.services,
            {
              id: serviceId,
              price: service.price.toString(),
              duration: service.duration,
              is_active: true
            }
          ]
        });
      }
    }
  };

  const handleAddBreak = (dayKey: string) => {
    const dayData = (formData.hours.regular_hours as any)?.[dayKey] || DEFAULT_HOURS[dayKey];
    const newBreak = { start_time: '12:00', end_time: '13:00' };
    const updated = {
      ...formData.hours.regular_hours,
      [dayKey]: {
        ...dayData,
        breaks: [...(dayData.breaks || []), newBreak]
      }
    };
    onFormChange({
      ...formData,
      hours: { ...formData.hours, regular_hours: updated }
    });
  };

  const handleRemoveBreak = (dayKey: string, breakIndex: number) => {
    const dayData = (formData.hours.regular_hours as any)?.[dayKey] || DEFAULT_HOURS[dayKey];
    const updatedBreaks = [...(dayData.breaks || [])];
    updatedBreaks.splice(breakIndex, 1);
    const updated = {
      ...formData.hours.regular_hours,
      [dayKey]: {
        ...dayData,
        breaks: updatedBreaks
      }
    };
    onFormChange({
      ...formData,
      hours: { ...formData.hours, regular_hours: updated }
    });
  };

  const handleBreakChange = (dayKey: string, breakIndex: number, field: 'start_time' | 'end_time', value: string) => {
    const dayData = (formData.hours.regular_hours as any)?.[dayKey] || DEFAULT_HOURS[dayKey];
    const updatedBreaks = [...(dayData.breaks || [])];
    updatedBreaks[breakIndex] = { ...updatedBreaks[breakIndex], [field]: value };
    const updated = {
      ...formData.hours.regular_hours,
      [dayKey]: {
        ...dayData,
        breaks: updatedBreaks
      }
    };
    onFormChange({
      ...formData,
      hours: { ...formData.hours, regular_hours: updated }
    });
  };

  const handleBusinessHoursToggle = () => {
    const useBusinessHours = !formData.hours.use_business_hours;
    onFormChange({
      ...formData,
      hours: {
        use_business_hours: useBusinessHours,
        regular_hours: useBusinessHours ? null : DEFAULT_HOURS,
        special_dates: formData.hours.special_dates || []
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.dispatchEvent(new CustomEvent('ui:service-modal', { detail: { open: true } }));
    } else {
      document.body.style.overflow = 'unset';
      window.dispatchEvent(new CustomEvent('ui:service-modal', { detail: { open: false } }));
      setPreviewUrl(null);
      setNewPassword('');
      setConfirmPassword('');
      setNewSpecialty('');
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.dispatchEvent(new CustomEvent('ui:service-modal', { detail: { open: false } }));
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const tabs = [
    { id: 'basic' as TabType, label: 'כללי', icon: User },
    { id: 'services' as TabType, label: 'שירותים', icon: Briefcase },
    { id: 'hours' as TabType, label: 'שעות', icon: Clock },
    { id: 'settings' as TabType, label: 'הגדרות', icon: SettingsIcon }
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/30 backdrop-blur-sm"
          onClick={onCancel}
        />
        <div className="absolute inset-0 flex items-end sm:items-center justify-center pointer-events-none">
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col overflow-hidden pointer-events-auto"
            style={{ height: '85vh', maxHeight: '85vh' }}
            onClick={(e) => e.stopPropagation()}
          >
          {/* iOS Style Header - Sticky with Blur */}
          <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
            <div className="flex items-center justify-between px-5 py-4">
              <button
                onClick={onCancel}
                className="text-blue-600 font-semibold text-[17px] active:opacity-50 transition-opacity"
                disabled={busy}
              >
                ביטול
              </button>
              <h2 className="text-[17px] font-semibold text-gray-900">
                {isEditing ? 'עריכת איש צוות' : 'איש צוות חדש'}
              </h2>
              <button
                onClick={handleSubmit}
                disabled={busy}
                className="text-blue-600 font-semibold text-[17px] active:opacity-50 transition-opacity disabled:opacity-30"
              >
                {busy ? 'שומר...' : 'שמור'}
              </button>
            </div>

            {/* iOS Segmented Control */}
            <div className="px-4 pb-3">
              <div className="bg-gray-100 rounded-xl p-1 flex gap-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                        activeTab === tab.id
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="p-5 space-y-6">
                {/* Profile Image - iOS Style */}
                <div className="flex flex-col items-center">
                  {previewUrl ? (
                    <div className="relative">
                      <img
                        src={previewUrl}
                        alt="תצוגה מקדימה"
                        className="h-32 w-32 rounded-full object-cover border-4 border-gray-100"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-1 -right-1 p-1.5 bg-red-500 text-white rounded-full shadow-lg active:scale-95 transition-transform"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="h-32 w-32 rounded-full bg-gray-100 flex items-center justify-center active:scale-95 transition-transform"
                    >
                      <div className="text-center">
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-500">הוסף תמונה</p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="sr-only"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </button>
                  )}
                </div>

                {/* iOS Style Form Groups */}
                <div className="bg-white rounded-2xl overflow-hidden border border-gray-200">
                  {/* Name */}
                  <div className="flex items-center px-4 py-3 border-b border-gray-100">
                    <label className="text-gray-900 font-medium w-24 text-[15px]">שם</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
                      className="flex-1 text-[15px] bg-transparent border-none outline-none text-right"
                      placeholder="שם מלא"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="flex items-center px-4 py-3 border-b border-gray-100">
                    <Mail className="h-5 w-5 text-gray-400 ml-3" />
                    <label className="text-gray-900 font-medium w-20 text-[15px]">אימייל</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => onFormChange({ ...formData, email: e.target.value })}
                      className="flex-1 text-[15px] bg-transparent border-none outline-none text-right"
                      placeholder="email@example.com"
                      required
                      disabled={isEditing}
                    />
                  </div>

                  {/* Phone */}
                  <div className="flex items-center px-4 py-3 border-b border-gray-100">
                    <Phone className="h-5 w-5 text-gray-400 ml-3" />
                    <label className="text-gray-900 font-medium w-20 text-[15px]">טלפון</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => onFormChange({ ...formData, phone: e.target.value })}
                      className="flex-1 text-[15px] bg-transparent border-none outline-none text-right"
                      placeholder="050-1234567"
                    />
                  </div>

                  {/* Title */}
                  <div className="flex items-center px-4 py-3">
                    <label className="text-gray-900 font-medium w-24 text-[15px]">תפקיד</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => onFormChange({ ...formData, title: e.target.value })}
                      className="flex-1 text-[15px] bg-transparent border-none outline-none text-right"
                      placeholder="מעצב שיער"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 p-4">
                  <label className="text-gray-900 font-medium text-[15px] mb-2 block">תיאור</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full text-[15px] bg-transparent border-none outline-none resize-none"
                    placeholder="תיאור קצר על איש הצוות..."
                  />
                </div>

                {/* Specialties */}
                <div>
                  <label className="text-gray-900 font-semibold text-[15px] mb-3 block">התמחויות</label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newSpecialty}
                      onChange={(e) => setNewSpecialty(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSpecialty())}
                      className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-[15px] outline-none focus:border-blue-500 focus:bg-white transition-all"
                      placeholder="הוסף התמחות..."
                    />
                    <button
                      type="button"
                      onClick={handleAddSpecialty}
                      className="px-5 bg-blue-600 text-white rounded-xl font-medium active:scale-95 transition-transform"
                    >
                      הוסף
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.specialties?.map((spec, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-800 rounded-full text-[13px] font-medium"
                      >
                        {spec}
                        <button
                          type="button"
                          onClick={() => handleRemoveSpecialty(idx)}
                          className="text-gray-500 active:text-gray-700"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <div className="p-5 space-y-3">
                <p className="text-[13px] text-gray-500 mb-2">בחר שירותים והגדר מחיר וזמן ספציפיים</p>
                {services.map((service) => {
                  const staffService = formData.services.find(s => s.id === service.id);
                  const isActive = staffService?.is_active || false;
                  const customPrice = staffService?.price;
                  const customDuration = staffService?.duration;

                  return (
                    <div
                      key={service.id}
                      className="w-full bg-white rounded-2xl border border-gray-200 overflow-hidden"
                    >
                      {/* Header - Service Toggle */}
                      <button
                        type="button"
                        onClick={() => handleServiceToggle(service.id)}
                        className="w-full p-4 active:scale-[0.98] transition-transform"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 text-right">
                            <h4 className="font-semibold text-gray-900 text-[15px]">{service.name_he}</h4>
                            <p className="text-[13px] text-gray-500 mt-0.5">
                              ברירת מחדל: {service.price}₪ • {service.duration}
                            </p>
                          </div>
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                              isActive ? 'bg-blue-600' : 'border-2 border-gray-300'
                            }`}
                          >
                            {isActive && <Check className="h-4 w-4 text-white" />}
                          </div>
                        </div>
                      </button>

                      {/* Custom Price & Duration - Show only if active */}
                      {isActive && (
                        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                          {/* Custom Price */}
                          <div>
                            <label className="text-[13px] text-gray-600 mb-1.5 block">
                              מחיר מותאם אישית (השאר ריק לברירת מחדל)
                            </label>
                            <input
                              type="number"
                              value={customPrice || ''}
                              onChange={(e) => {
                                const updatedServices = formData.services.map(s =>
                                  s.id === service.id
                                    ? { ...s, price: e.target.value || '' }
                                    : s
                                );
                                onFormChange({ ...formData, services: updatedServices });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-[15px] outline-none focus:border-blue-500 focus:bg-white"
                              placeholder={`ברירת מחדל: ${service.price}₪`}
                              min="0"
                            />
                          </div>

                          {/* Custom Duration */}
                          <div>
                            <label className="text-[13px] text-gray-600 mb-1.5 block">
                              זמן מותאם אישית (השאר ריק לברירת מחדל)
                            </label>
                            <input
                              type="text"
                              value={customDuration || ''}
                              onChange={(e) => {
                                const updatedServices = formData.services.map(s =>
                                  s.id === service.id
                                    ? { ...s, duration: e.target.value || '' }
                                    : s
                                );
                                onFormChange({ ...formData, services: updatedServices });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-[15px] outline-none focus:border-blue-500 focus:bg-white"
                              placeholder={`ברירת מחדל: ${service.duration}`}
                            />
                            <p className="text-[11px] text-gray-400 mt-1">פורמט: HH:MM:SS (לדוגמה: 01:30:00)</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Hours Tab */}
            {activeTab === 'hours' && (
              <div className="p-5 space-y-4">
                <button
                  type="button"
                  onClick={handleBusinessHoursToggle}
                  className="w-full bg-white rounded-2xl p-4 border border-gray-200 active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 text-right">
                      <Clock className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-semibold text-gray-900 text-[15px]">שעות העסק</p>
                        <p className="text-[13px] text-gray-500">השתמש בשעות הכלליות</p>
                      </div>
                    </div>
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                        formData.hours.use_business_hours ? 'bg-blue-600' : 'border-2 border-gray-300'
                      }`}
                    >
                      {formData.hours.use_business_hours && <Check className="h-4 w-4 text-white" />}
                    </div>
                  </div>
                </button>

                {!formData.hours.use_business_hours && (
                  <div className="space-y-2">
                    <p className="text-[13px] font-semibold text-gray-900 mb-2">שעות מותאמות</p>
                    {DAYS_ORDER.map((day) => {
                      const dayData = (formData.hours.regular_hours as any)?.[day.key] || DEFAULT_HOURS[day.key];
                      return (
                        <div key={day.key} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                          <div className="flex items-center justify-between px-4 py-3">
                            <span className="font-medium text-gray-900 text-[15px]">{day.label}</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={dayData.is_active}
                                onChange={(e) => {
                                  const updated = {
                                    ...formData.hours.regular_hours,
                                    [day.key]: { ...dayData, is_active: e.target.checked }
                                  };
                                  onFormChange({
                                    ...formData,
                                    hours: { ...formData.hours, regular_hours: updated }
                                  });
                                }}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 transition-all relative">
                                <div className="absolute top-0.5 right-0.5 bg-white h-5 w-5 rounded-full transition-transform peer-checked:translate-x-5 shadow-sm"></div>
                              </div>
                            </label>
                          </div>
                          {dayData.is_active && (
                            <div className="px-4 pb-3 space-y-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="time"
                                  value={dayData.start_time}
                                  onChange={(e) => {
                                    const updated = {
                                      ...formData.hours.regular_hours,
                                      [day.key]: { ...dayData, start_time: e.target.value }
                                    };
                                    onFormChange({
                                      ...formData,
                                      hours: { ...formData.hours, regular_hours: updated }
                                    });
                                  }}
                                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[15px]"
                                />
                                <span className="text-gray-400">-</span>
                                <input
                                  type="time"
                                  value={dayData.end_time}
                                  onChange={(e) => {
                                    const updated = {
                                      ...formData.hours.regular_hours,
                                      [day.key]: { ...dayData, end_time: e.target.value }
                                    };
                                    onFormChange({
                                      ...formData,
                                      hours: { ...formData.hours, regular_hours: updated }
                                    });
                                  }}
                                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[15px]"
                                />
                              </div>

                              {dayData.breaks && dayData.breaks.length > 0 && (
                                <div className="space-y-2 mt-2">
                                  <p className="text-xs text-gray-500 font-medium">הפסקות:</p>
                                  {dayData.breaks.map((breakItem: any, breakIndex: number) => (
                                    <div key={breakIndex} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
                                      <input
                                        type="time"
                                        value={breakItem.start_time}
                                        onChange={(e) => handleBreakChange(day.key, breakIndex, 'start_time', e.target.value)}
                                        className="flex-1 px-2 py-1.5 bg-white border border-gray-200 rounded text-xs"
                                      />
                                      <span className="text-gray-400 text-xs">-</span>
                                      <input
                                        type="time"
                                        value={breakItem.end_time}
                                        onChange={(e) => handleBreakChange(day.key, breakIndex, 'end_time', e.target.value)}
                                        className="flex-1 px-2 py-1.5 bg-white border border-gray-200 rounded text-xs"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveBreak(day.key, breakIndex)}
                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg active:scale-95 transition-all"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <button
                                type="button"
                                onClick={() => handleAddBreak(day.key)}
                                className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 active:scale-95 transition-all"
                              >
                                <Plus className="h-3.5 w-3.5" />
                                הוסף הפסקה
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="p-5 space-y-4">
                {!isEditing ? (
                  <div className="bg-white rounded-2xl border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Key className="h-5 w-5 text-blue-600" />
                      <label className="text-gray-900 font-semibold text-[15px]">הגדרת סיסמה</label>
                    </div>
                    <p className="text-[13px] text-gray-500 mb-3">הגדר סיסמה לאיש הצוות החדש</p>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => onFormChange({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-[15px] outline-none focus:border-blue-500 focus:bg-white transition-all mb-2"
                      placeholder="לפחות 6 תווים"
                      minLength={6}
                    />
                    <p className="text-[11px] text-gray-400">הסיסמה חייבת להכיל לפחות 6 תווים</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Lock className="h-5 w-5 text-blue-600" />
                      <label className="text-gray-900 font-semibold text-[15px]">שינוי סיסמה</label>
                    </div>
                    <p className="text-[13px] text-gray-500 mb-3">שנה את סיסמת איש הצוות</p>

                    <div className="space-y-2 mb-3">
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-[15px] outline-none focus:border-blue-500 focus:bg-white transition-all"
                        placeholder="סיסמה חדשה (לפחות 6 תווים)"
                        minLength={6}
                      />
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-[15px] outline-none focus:border-blue-500 focus:bg-white transition-all"
                        placeholder="אימות סיסמה"
                      />
                    </div>

                    {newPassword && confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-[12px] text-red-500 mb-2">הסיסמאות אינן תואמות</p>
                    )}

                    <button
                      type="button"
                      onClick={async () => {
                        if (!newPassword || newPassword.length < 6) {
                          toast.error('הסיסמה חייבת להכיל לפחות 6 תווים');
                          return;
                        }
                        if (newPassword !== confirmPassword) {
                          toast.error('הסיסמאות אינן תואמות');
                          return;
                        }
                        try {
                          const { data: { session } } = await supabase.auth.getSession();
                          const response = await fetch(
                            'https://nkuqcyelxgyihrxyvitb.supabase.co/functions/v1/update-staff-password',
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${session?.access_token}`
                              },
                              body: JSON.stringify({
                                staff_email: formData.email,
                                new_password: newPassword
                              })
                            }
                          );
                          if (!response.ok) throw new Error('שגיאה בעדכון סיסמה');
                          toast.success('הסיסמה עודכנה בהצלחה');
                          setNewPassword('');
                          setConfirmPassword('');
                        } catch (err: any) {
                          toast.error(err.message || 'שגיאה בעדכון סיסמה');
                        }
                      }}
                      disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword}
                      className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      עדכן סיסמה
                    </button>

                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-[13px] text-gray-500 mb-2">או שלח לינק לאיפוס סיסמה</p>
                      <button
                        type="button"
                        onClick={async () => {
                          setSendingResetLink(true);
                          try {
                            const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
                              redirectTo: `${window.location.origin}/update-password`
                            });
                            if (error) throw error;
                            toast.success('לינק לאיפוס סיסמה נשלח בהצלחה');
                          } catch (err: any) {
                            toast.error(err.message || 'שגיאה בשליחת לינק');
                          } finally {
                            setSendingResetLink(false);
                          }
                        }}
                        disabled={sendingResetLink}
                        className="w-full px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <Send className="h-4 w-4" />
                        {sendingResetLink ? 'שולח...' : 'שלח לינק לאיפוס'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Mail className="h-5 w-5 text-blue-600" />
                    <label className="text-gray-900 font-semibold text-[15px]">סוג התחברות</label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-[15px] text-gray-700">אימייל וסיסמה</p>
                      <p className="text-[13px] text-gray-500 mt-0.5">התחברות סטנדרטית עם אימייל וסיסמה</p>
                    </div>
                    <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-[12px] font-medium">
                      פעיל
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <label className="text-gray-900 font-semibold text-[15px] mb-3 block">זמן מנוחה (דקות)</label>
                  <input
                    type="number"
                    value={formData.settings?.rest_time || 15}
                    onChange={(e) =>
                      onFormChange({
                        ...formData,
                        settings: { ...formData.settings, rest_time: parseInt(e.target.value) || 15 }
                      })
                    }
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-[15px] outline-none focus:border-blue-500 focus:bg-white transition-all"
                    min="0"
                  />
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-[15px]">גלוי בדף ציבורי</p>
                      <p className="text-[13px] text-gray-500 mt-0.5">הצג בדף הזמנות</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.settings?.visible_in_public !== false}
                        onChange={(e) =>
                          onFormChange({
                            ...formData,
                            settings: { ...formData.settings, visible_in_public: e.target.checked }
                          })
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 transition-all relative">
                        <div className="absolute top-0.5 right-0.5 bg-white h-5 w-5 rounded-full transition-transform peer-checked:translate-x-5 shadow-sm"></div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </form>
        </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}
